import React, { Fragment } from 'react';
import { View } from '@react-pdf/renderer';
import { get, round, toString, values } from 'lodash';
import { gradients } from '../../utilities/color';
import { toFeet, toMeter } from '../../utilities/format';
import devicePlots from '../../globalConstants/devicePlots';

// Import MUI components
import MuiFloorPlan from './FloorPlan';
import MuiCanvas from './Canvas';
import MuiScale from './Scale';

/**
 * Interface for area dimensions
 */
interface Dimensions {
  length: number;
  width: number;
  [key: string]: any;
}

/**
 * Interface for an area
 */
interface Area {
  id: string;
  location: {
    top: number;
    left: number;
    [key: string]: number;
  };
  dimensions: Dimensions;
  [key: string]: any;
}

/**
 * Interface for areas object
 */
interface Areas {
  [key: string]: Area;
}

/**
 * Interface for content
 */
interface Content {
  areas: Areas;
  dimensions?: Dimensions;
  isMeter?: boolean;
  [key: string]: any;
}

/**
 * Interface for plot config properties
 */
interface PlotConfig {
  minLevel: number;
  maxLevel: number;
  average?: any; // Using any to allow null/undefined without TypeScript errors
  deviation?: any; // Using any to allow null/undefined without TypeScript errors
  units?: string;
  labels?: Record<string, number>;
  target?: number;
  roundTo?: number;
  plotType: string;
  label: string;
  [key: string]: any;
}

/**
 * Interface for bounding box
 */
interface BoundingBox {
  width: number;
  height: number;
}

/**
 * Interface for constants
 */
interface Constants {
  defaultPlots: {
    [key: string]: string;
  };
  reportPlotElements: string[];
  [key: string]: any;
}

/**
 * Interface for MuiPlot component props
 */
interface MuiPlotProps {
  areaId?: string | null;
  content: Content;
  device: string;
  constants: Constants;
  preGeneratedImages?: Record<string, any[]>;
  [key: string]: any;
}

/**
 * MUI Plot section component that replaces the styled-components Plot section
 * 
 * This component provides a Material UI equivalent of the original Plot section component
 * with similar props and functionality. It's used for PDF generation.
 * 
 * @param {MuiPlotProps} props - Component props
 * @returns {JSX.Element} MUI Plot section component
 */
const MuiPlot: React.FC<MuiPlotProps> = (props) => {
  const { areaId = null, content, device, constants: { defaultPlots } } = props;
  
  // Make the container 
  const inContainer = areaId ? false : true;
  const areas = content.areas;
  const dimensions = content.dimensions ?? areas[areaId as string]?.dimensions;
  const scale = 532 / Math.max(dimensions.length, dimensions.width);
  const bbox: BoundingBox = { 
    width: ((inContainer ? dimensions.length : areas?.[areaId as string]?.dimensions.length) * scale) + 2, 
    height: ((inContainer ? dimensions.width : areas?.[areaId as string]?.dimensions.width) * scale) + 2 
  };

  // Get the plotProperties
  const plot = defaultPlots[device];
  const baseProps = devicePlots[device].plots[plot] as any;
  const { 
    minLevel: min, 
    maxLevel: max, 
    average: avg, 
    deviation: std, 
    roundTo = 1, 
    target: tgt, 
    units: legendUnits, 
    labels: legendLabels 
  } = baseProps;
  
  const areaArray = inContainer ? values(areas) : [areas[areaId as string]];
  
  // Handle values that might be paths in the object or direct values
  const minLevel = isFinite(min as number) 
    ? min as number 
    : Math.min(...areaArray.map(obj => get(obj, min as string)).filter(Boolean));
    
  const maxLevel = isFinite(max as number) 
    ? max as number 
    : Math.max(...areaArray.map(obj => get(obj, max as string)).filter(Boolean));
  
  // Set reasonable defaults if values can't be resolved
  let resolvedMinLevel = isFinite(minLevel) ? minLevel : 0;
  let resolvedMaxLevel = isFinite(maxLevel) ? maxLevel : 100;
  
  // Device-specific defaults
  if (device === 'microphones') {
    resolvedMinLevel = isFinite(minLevel) ? minLevel : 0;
    resolvedMaxLevel = isFinite(maxLevel) ? maxLevel : 6;
  } else if (device === 'speakers') {
    resolvedMinLevel = isFinite(minLevel) ? minLevel : 60;
    resolvedMaxLevel = isFinite(maxLevel) ? maxLevel : 110;
  }
    
  const useDistanceUnits = !legendUnits && !legendLabels;
  
  let labels = legendLabels;
  let units = legendUnits;
  
  if (useDistanceUnits) {
    units = content.isMeter ? "Meters" : "Feet";
    const step = content.isMeter ? 1 : 5;
    const distanceArray = Array.from(
      { length: Math.ceil(toFeet(maxLevel, content.isMeter) / step) },
      (_, i) => i * step
    ).slice(1);

    // Add unit symbols to labels for camera distance plots
    const unitSymbol = content.isMeter ? "m" : "'";
    labels = distanceArray.reduce<Record<string, number>>(
      (acc, curr) => ({ ...acc, [`${toString(curr)}${unitSymbol}`]: toMeter(curr, content.isMeter) }),
      {}
    );
  }
  
  // Handle target value
  const target = tgt 
    ? Math.min(...areaArray.map(obj => get(obj, tgt as string)).filter(Boolean)) 
    : minLevel * 0.35 + maxLevel * 0.65;
  
  // Calculate average
  let average: any = areaArray.map(obj => obj[avg as string])
    .filter(Boolean)
    .reduce((avg, value, _, { length }) => avg + value / length, 0);
  
  average = average === 0 ? null : round(average, roundTo);
  
  // Calculate deviation
  let deviation: any = round(
    areaArray.map(obj => obj[std as string])
      .reduce((avg, value, _, { length }) => avg + value / length, 0), 
    2
  );
  
  deviation = isFinite(deviation) ? deviation : null;

  // Construct plot properties with resolved values
  const plotProps: PlotConfig = {
    ...baseProps,
    minLevel: resolvedMinLevel,
    maxLevel: resolvedMaxLevel,
    labels,
    units,
    target: isFinite(target) ? target : (device === 'microphones' ? 2.4755 : device === 'speakers' ? 90 : resolvedMinLevel * 0.35 + resolvedMaxLevel * 0.65),
    average,
    deviation,
    plotType: baseProps.plotType || (baseProps.type === 'distance' ? 'micDistance' : 'SPL'),
    label: device === 'microphones' ? 'Distance from nearest mic' :
           device === 'speakers' ? 'Relative Average Level' :
           device === 'cameras' ? `Distance from camera (${units})` :
           units || 'Value',
    // Include the actual pointToPixelValue function from devicePlots
    pointToPixelValue: baseProps.pointToPixelValue
  };
  
  // Get gradient
  const gradient = gradients[plotProps.plotType](plotProps);

  // Combine props
  const newprops = {
    inContainer,
    scale,
    bbox,
    ...props
  };

  return (
    <Fragment>
      <View style={{width: bbox.width, height: bbox.height}}>
        <MuiCanvas {...newprops} plotProps={plotProps as any} gradient={gradient as any} />
        <MuiFloorPlan {...newprops} />
      </View>
      <MuiScale plotProps={plotProps as any} gradient={gradient as any} bbox={bbox} />
    </Fragment>
  );
};

export default MuiPlot;
